import type { ReactNode } from 'react';
import { logout } from '../../services/auth';
import { Button } from '../ui/Button';
import { AlertTriangle, Lock } from '../ui/icons';

/** Full-screen gates shown instead of the app shell when the account itself cannot use Tasky. */
function Gate({ icon, title, children, action }: { icon: ReactNode; title: string; children: ReactNode; action: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
      <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg dark:bg-gray-800">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">{icon}</div>
        <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
        <p className="mb-6 text-gray-600 dark:text-gray-400">{children}</p>
        {action}
      </div>
    </div>
  );
}

/** Signed in with an email domain that is not allowed. */
export function AccountNotAuthorized() {
  return (
    <Gate
      icon={<AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />}
      title="Account not authorized"
      action={
        <Button variant="danger" fullWidth size="lg" onClick={() => logout()}>
          Sign out
        </Button>
      }
    >
      Your account is not authorized to use this application. Only users with an approved organizational email domain can sign in.
    </Gate>
  );
}

/** Signed in, allowed domain, but an owner has not activated the account yet. */
export function DisabledAccount() {
  return (
    <Gate
      icon={<Lock className="h-8 w-8 text-amber-600 dark:text-amber-400" />}
      title="Account not activated"
      action={
        <Button variant="secondary" fullWidth size="lg" onClick={() => logout()}>
          Sign out
        </Button>
      }
    >
      Your account has been registered but not activated yet. Ask an owner to enable it on the Users page.
    </Gate>
  );
}
